import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CopyResultButton } from "@/components/CopyResultButton";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function BreakEvenCalculator() {
  const { t } = useTranslation();
  const [fixedCosts, setFixedCosts] = useState("");
  const [variableCostPerUnit, setVariableCostPerUnit] = useState("");
  const [pricePerUnit, setPricePerUnit] = useState("");
  const [result, setResult] = useState<{ units: number; revenue: number } | null>(null);

  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: t('nav.home'), url: '/' },
      { name: t('categories.finance'), url: '/finance' },
      { name: "Break-Even Calculator", url: '/calculator/break-even' }
    ], lang),
    (lang) => calculatorToolSchema(
      "Break-Even Calculator",
      "Calculate when total revenue equals total costs. Enter fixed costs, variable cost per unit, and price per unit to find break-even point.",
      "https://calclab.app/calculator/break-even",
      lang
    )
  ]);

  const calculate = () => {
    const FC = parseFloat(fixedCosts);
    const VC = parseFloat(variableCostPerUnit);
    const P = parseFloat(pricePerUnit);

    if (isNaN(FC) || isNaN(VC) || isNaN(P) || FC < 0 || VC < 0 || P <= 0 || P <= VC) {
      toast.error("Please enter valid values (price must be greater than variable cost)");
      return;
    }

    const breakEvenUnits = FC / (P - VC);
    const breakEvenRevenue = breakEvenUnits * P;

    setResult({ units: breakEvenUnits, revenue: breakEvenRevenue });
    toast.success("Break-even point calculated!");
  };

  const reset = () => {
    setFixedCosts("");
    setVariableCostPerUnit("");
    setPricePerUnit("");
    setResult(null);
  };

  return (
    <PageLayout>
      <Card>
        <CardHeader>
          <CardTitle>Break-Even Analysis</CardTitle>
          <CardDescription>
            Calculate when total revenue equals total costs
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="fixedCosts">Fixed Costs ($)</Label>
            <Input
              id="fixedCosts"
              type="number"
              placeholder="50000"
              value={fixedCosts}
              onChange={(e) => setFixedCosts(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="variableCostPerUnit">Variable Cost per Unit ($)</Label>
            <Input
              id="variableCostPerUnit"
              type="number"
              placeholder="20"
              value={variableCostPerUnit}
              onChange={(e) => setVariableCostPerUnit(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="pricePerUnit">Price per Unit ($)</Label>
            <Input
              id="pricePerUnit"
              type="number"
              placeholder="50"
              value={pricePerUnit}
              onChange={(e) => setPricePerUnit(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={calculate}>Calculate</Button>
            <Button onClick={reset} variant="outline">Reset</Button>
          </div>
          {result && (
            <>
              <div className="mt-4 p-4 bg-muted rounded-lg space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-lg break-all overflow-wrap-anywhere">
                  <strong>Break-Even Units:</strong> {result.units.toFixed(2)}
                </p>
                <CopyResultButton value={result.units.toFixed(2)} />
              </div>
              <div className="flex items-center justify-between">
                <p className="text-lg break-all overflow-wrap-anywhere">
                  <strong>Break-Even Revenue:</strong> ${result.revenue.toFixed(2)}
                </p>
                <CopyResultButton value={result.revenue.toFixed(2)} />
              </div>
            </div>

            <CalculatorSuperCalcCTA config={calculatorCTAs["break-even"]} trigger={result?.units} />
            </>
          )}
        </CardContent>
      </Card>
    </PageLayout>
  );
}
